import { Box, CircularProgress } from '@mui/material';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import TopNav from '../components/TopNav';
import Footer from '../components/navigation/Footer';
import ConsentBanner from '../components/marketing/ConsentBanner';
import { PendingRegistrationBanner } from '../components/PendingRegistrationBanner';

// Core pages - loaded immediately
import HomePage from '../pages/HomePage';
import GroupsPage from '../pages/GroupsPage';
import AllEventsPage from '../pages/AllEventsPage';
import GroupDetailPage from '../pages/GroupDetailPage';
import MeetingDetailPage from '../pages/MeetingDetailPage';
import AboutPage from '../pages/AboutPage';

// Admin/Organizer features - code split for better performance
const AdminDashboard = lazy(() => import('../pages/AdminDashboard'));
const UserManagementPage = lazy(() => import('../pages/UserManagementPage'));
const OrganizerHQPage = lazy(() => import('../pages/OrganizerHQPage'));
const BroadcastPage = lazy(() => import('../pages/BroadcastPage'));
const EditMeetingPage = lazy(() => import('../pages/EditMeetingPage'));

// User-specific pages - code split
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));

// Marketing pages - code split
const FAQPage = lazy(() => import('../pages/marketing/FAQPage'));
const CommunityMissionPage = lazy(() => import('../pages/CommunityMissionPage'));

// Legal pages - code split
const TermsOfUsePage = lazy(() => import('../pages/legal/TermsOfUsePage'));
const PrivacyPolicyPage = lazy(() => import('../pages/legal/PrivacyPolicyPage'));
const CommunityGuidelinesPage = lazy(
  () => import('../pages/legal/CommunityGuidelinesPage'),
);

const App = () => (
  <HelmetProvider>
    <BrowserRouter>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <TopNav />
        <PendingRegistrationBanner />
        <Box component="main" sx={{ flex: 1 }}>
          <Suspense
            fallback={
              <Box
                alignItems="center"
                display="flex"
                height="60vh"
                justifyContent="center"
              >
                <CircularProgress />
              </Box>
            }
          >
            <Routes>
              <Route element={<HomePage />} path="/" />
              <Route element={<AllEventsPage />} path="/events" />
              <Route element={<GroupsPage />} path="/groups" />
              <Route element={<GroupDetailPage />} path="/groups/:groupId" />
              <Route element={<MeetingDetailPage />} path="/meetings/:meetingId" />
              <Route element={<EditMeetingPage />} path="/meetings/:meetingId/edit" />
              <Route element={<AdminDashboard />} path="/admin" />
              <Route element={<UserManagementPage />} path="/admin/users" />
              <Route element={<BroadcastPage />} path="/broadcast" />
              <Route element={<NotificationsPage />} path="/notifications" />
              <Route element={<ProfilePage />} path="/profile" />
              <Route element={<OrganizerHQPage />} path="/organizer/hq" />
              <Route element={<AboutPage />} path="/about" />
              <Route element={<CommunityMissionPage />} path="/community-mission" />
              <Route element={<FAQPage />} path="/faq" />
              <Route element={<TermsOfUsePage />} path="/legal/terms" />
              <Route element={<PrivacyPolicyPage />} path="/legal/privacy" />
              <Route
                element={<CommunityGuidelinesPage />}
                path="/legal/community-guidelines"
              />
              <Route element={<Navigate to="/" />} path="*" />
            </Routes>
          </Suspense>
        </Box>
        <Footer />
        <ConsentBanner />
      </Box>
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
